import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import {
  createPaymentRequest,
  deleteDraftPaymentRequest,
  fetchPaymentRequestDetail,
  submitPaymentRequest,
  updatePaymentRequest,
} from '#/api/fmsMgmt/paymentRequest';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PaymentRequestLinesEditor from '#/modules/finMgmt/claims/components/PaymentRequestLinesEditor';
import PaymentRequestVouchersSection from '#/modules/finMgmt/claims/components/PaymentRequestVouchersSection';
import PaymentRequestStatusBadge from '#/modules/finMgmt/claims/components/PaymentRequestStatusBadge';
import {
  getPaymentRequestApiErrorMessage,
  isPaymentRequestVersionConflict,
} from '#/modules/finMgmt/claims/helper/apiError';
import {
  canDeletePaymentRequest,
  canEditPaymentRequest,
  FMS_ROUNDING_MODE_OPTIONS,
} from '#/modules/finMgmt/claims/helper/paymentRequestStatus';
import {
  PAYMENT_REQUEST_FIELD_LABELS,
  PAYMENT_REQUEST_FORM_DEFAULTS,
  paymentRequestFormSchema,
} from '#/modules/finMgmt/claims/schemas/paymentRequestFormSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { toast } from '#/utils/toast';
import {
  paymentRequestDetailQueryKey,
  paymentRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';

const BACK_TO = ROUTES.personalCenterClaimsPanel;

const DELETE_CONFIRM = {
  title: '刪除草稿',
  message: '確定刪除此請款草稿？此操作無法復原。',
  confirmText: '刪除',
  destructive: true,
};

const SUBMIT_CONFIRM = {
  title: '提交請款',
  message: '確定提交此請款單？提交後將進入審核流程。',
  confirmText: '提交',
};

function buildPaymentRequestPayload(values, updatedAt) {
  const payload = {
    paymentReason: values.paymentReason,
    roundingMode: values.roundingMode,
    remark: values.remark || null,
    lines: (values.lines || []).map((line) => ({
      itemName: line.itemName,
      purpose: line.purpose,
      unitPrice: Number(line.unitPrice),
      qty: Number(line.qty),
    })),
    voucherIds: values.voucherIds || [],
  };
  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }
  return payload;
}

const CreateEditPaymentRequestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const { confirm, confirmDialog } = useConfirmDialog();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    // 新增／編輯只在個人中心 → `/self`
    queryKey: paymentRequestDetailQueryKey(id, FMS_READ_SCOPE.SELF),
    queryFn: () => fetchPaymentRequestDetail(id, { scope: FMS_READ_SCOPE.SELF }),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status ?? null;
  const totalAmount = detail?.totalAmount ?? null;
  const requestNo = detail?.requestNo ?? null;

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createPaymentRequest,
    updateFn: updatePaymentRequest,
    buildPayload: (values) => buildPaymentRequestPayload(
      values,
      id ? updatedAtRef.current : null,
    ),
    listQueryKeyPrefix: paymentRequestListQueryKeyPrefix,
    detailQueryKey: paymentRequestDetailQueryKey,
    getDetailPath: ROUTES.personalCenterClaimDetail,
    afterUpdateNavigateTo: id ? ROUTES.personalCenterClaimDetail(id) : null,
    getErrorMessage: (error) => (
      getPaymentRequestApiErrorMessage(error, '儲存失敗')
    ),
    isVersionConflict: isPaymentRequestVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(paymentRequestFormSchema),
    defaultValues: PAYMENT_REQUEST_FORM_DEFAULTS,
  });

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();

    const voucherIds = Array.isArray(detail.voucherIds) && detail.voucherIds.length > 0
      ? detail.voucherIds
      : (detail.vouchers || []).map((item) => item.id).filter(Boolean);

    reset({
      paymentReason: detail.paymentReason ?? '',
      roundingMode: detail.roundingMode
        ?? PAYMENT_REQUEST_FORM_DEFAULTS.roundingMode,
      remark: detail.remark ?? '',
      lines: (detail.lines || []).length > 0
        ? detail.lines.map((line) => ({
          itemName: line.itemName ?? '',
          purpose: line.purpose ?? '',
          unitPrice: line.unitPrice ?? '',
          qty: line.qty ?? 1,
        }))
        : PAYMENT_REQUEST_FORM_DEFAULTS.lines,
      voucherIds,
    });
  }, [detail, reset, resetValidationSummary]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteDraftPaymentRequest(id),
    onSuccess: async () => {
      toast.success('草稿已刪除');
      await queryClient.invalidateQueries({
        queryKey: paymentRequestListQueryKeyPrefix(),
      });
      navigate(BACK_TO);
    },
    onError: (error) => {
      toast.error(getPaymentRequestApiErrorMessage(error, '刪除失敗'));
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitPaymentRequest(id, {
      updatedAt: updatedAtRef.current,
    }),
    onSuccess: async (result) => {
      toast.success(result?.message || '提交成功');
      await queryClient.invalidateQueries({
        queryKey: paymentRequestListQueryKeyPrefix(),
      });
      await queryClient.invalidateQueries({
        queryKey: paymentRequestDetailQueryKey(id),
      });
      await detailQuery.refetch();
    },
    onError: (error) => {
      toast.error(getPaymentRequestApiErrorMessage(error, '提交失敗'));
      if (isPaymentRequestVersionConflict(error)) {
        detailQuery.refetch();
      }
    },
  });

  const isEditable = canEditPaymentRequest(status);
  const showDelete = isEdit && canDeletePaymentRequest(status);
  const showSubmit = isEdit && canEditPaymentRequest(status);
  const isBusy = isPending
    || deleteMutation.isPending
    || submitMutation.isPending;

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    if (!isEditable) {
      return;
    }
    markSubmitAttempted();
    save(values);
  }

  async function handleDelete() {
    const ok = await confirm(DELETE_CONFIRM);
    if (!ok) {
      return;
    }
    deleteMutation.mutate();
  }

  async function handleSubmitRequest() {
    const ok = await confirm(SUBMIT_CONFIRM);
    if (!ok) {
      return;
    }
    submitMutation.mutate();
  }

  const pageTitle = isEdit ? '編輯請款' : '新增請款';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;

  return (
    <PageShell variant="formTall">
      {confirmDialog}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-5xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">
                {pageTitle}
              </span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={BACK_TO}>返回</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getPaymentRequestApiErrorMessage(
                detailQuery.error,
                '找不到請款資料',
              )}
            </p>
          ) : (
            <>
              {isEdit ? (
                <p
                  className={cn(
                    'mb-3 flex flex-wrap items-center gap-2',
                    'text-sm text-muted-foreground',
                  )}
                >
                  <span>
                    單號：
                    {requestNo || '（提交後產生）'}
                  </span>
                  {status ? (
                    <PaymentRequestStatusBadge status={status} size="sm" />
                  ) : null}
                  {!isEditable ? <span>（唯讀）</span> : null}
                </p>
              ) : null}

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={PAYMENT_REQUEST_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className={cn(
                  'sheet create_edit create_edit_form',
                  'create_edit_form--page-scroll',
                )}
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    {/* 兩欄：事由｜進位法、總額；備註跨整列。窄螢幕單欄 */}
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-x-4 gap-y-3',
                        '@2xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingInput
                        id="paymentReason"
                        type="text"
                        label="請款事由"
                        register={register}
                        errors={errors}
                        readOnly={!isEditable}
                      />
                      <FloatingSelect
                        id="roundingMode"
                        label="進位法"
                        options={FMS_ROUNDING_MODE_OPTIONS}
                        control={control}
                        errors={errors}
                        isDisabled={!isEditable}
                      />
                      <FloatingInput
                        id="totalAmountDisplay"
                        type="text"
                        label="總額"
                        value={isEdit ? formatFmsAmount(totalAmount) : '存檔後由系統計算'}
                        readOnly
                      />
                      <div className="@2xl/sheet:col-span-2">
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                          readOnly={!isEditable}
                        />
                      </div>
                    </div>
                  </section>

                  <PaymentRequestVouchersSection
                    control={control}
                    errors={errors}
                    readOnly={!isEditable}
                    initialVouchers={detail?.vouchers}
                  />

                  <PaymentRequestLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    readOnly={!isEditable}
                  />
                </div>

                <div
                  className={cn(
                    'create_edit_form__actions',
                    'flex flex-wrap justify-end gap-2',
                  )}
                >
                  <Button type="button" variant="outline" asChild>
                    <Link to={BACK_TO}>取消</Link>
                  </Button>
                  {showDelete ? (
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={isBusy}
                      onClick={handleDelete}
                    >
                      {deleteMutation.isPending ? '刪除中…' : '刪除草稿'}
                    </Button>
                  ) : null}
                  {showSubmit ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={isBusy}
                      onClick={handleSubmitRequest}
                    >
                      {submitMutation.isPending ? '提交中…' : '提交'}
                    </Button>
                  ) : null}
                  {isEditable ? (
                    <Button type="submit" disabled={isBusy}>
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  ) : null}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditPaymentRequestPage;
